import useProductVariant from 'src/api/inventory-management/products/useProductVariant';
import { Badge } from '@/components/ui/badge';
import { T, Var } from 'gt-react';

interface LineItemProductInfoProps {
  productId?: string | null;
  productVariantId?: string | null;
  productName?: string | null;
  productSku?: string | null;
  productVariantSku?: string | null;
}

/**
 * Renders product/variant info for a finalized SO or PO line item.
 * Uses backend-provided name/SKU as the primary display, then enriches
 * with variant NAMEINVC and active status fetched from the product API.
 */
export function LineItemProductInfo({
  productVariantId,
  productName,
  productSku,
  productVariantSku,
}: LineItemProductInfoProps) {
  const { data: variantData, isLoading: isVariantLoading } = useProductVariant(
    productVariantId ?? '',
    { enabled: !!productVariantId, includeInactive: true },
  );

  const variant = variantData?.productVariant;
  const variantDisplayName =
    (variant?.originMetadata?.NAMEINVC as string | undefined) ??
    variant?.sku ??
    productVariantSku;
  const isActive = variant?.active;
  const originProductId = variant?.originMetadata?.PRODUCT_ID;

  return (
    <div className="flex flex-col gap-0.5">
      {/* Primary: product name */}
      <span className="font-medium text-sm">
        {productName ?? (
          <span className="text-muted-foreground">
            <T context="line item product info unknown product">
              Unknown Product
            </T>
          </span>
        )}
      </span>

      {/* Secondary: product SKU */}
      {productSku && (
        <span className="text-xs text-muted-foreground">
          <T context="line item product SKU label">
            SKU: <Var>{productSku}</Var>
          </T>
        </span>
      )}

      {/* Variant row (loaded from API for NAMEINVC + active status) */}
      {productVariantId && (
        <div className="flex items-center gap-1.5 mt-0.5">
          {isVariantLoading ? (
            <span className="text-xs text-muted-foreground italic">
              <T context="line item variant loading">Loading variant...</T>
            </span>
          ) : variantDisplayName ? (
            <>
              <span className="text-xs text-muted-foreground italic">
                <T context="line item variant name">
                  Variant: <Var>{variantDisplayName}</Var>
                  <Var>
                    {originProductId ? ` (${String(originProductId)})` : ''}
                  </Var>
                </T>
              </span>
              {isActive === false && (
                <Badge
                  variant="outline"
                  className="text-[10px] px-1 py-0 h-4 border-red-400 text-red-600 bg-red-50"
                >
                  <T context="line item variant inactive badge">Inactive</T>
                </Badge>
              )}
            </>
          ) : productVariantSku ? (
            <span className="text-xs text-muted-foreground italic">
              <T context="line item variant SKU label">
                Variant SKU: <Var>{productVariantSku}</Var>
              </T>
            </span>
          ) : null}
        </div>
      )}
    </div>
  );
}
